import type { BuiltInTool } from './types'

const GATE_SUMMARY = 'os.set_gate_summary'
const DECIDE_GATE = 'os.decide_gate'

/** What the model reads when a gated call is refused because the run has not written a gate summary yet. */
export const GATE_SUMMARY_HINT = `Write the summary first: call ${GATE_SUMMARY} with markdown bullets (each line starting "- ") covering what this change does and anything to watch out for, then call the gated tool again.`

/**
 * The summary the person deciding a run's next gate reads before deciding, and the decision itself for a person's
 * own agent: `os.decide_gate` is the Needs you card over the API and the OS MCP server, under the person's own
 * `gates:write`, recorded as them. A run never holds it: seeing a waiting gate (`os.list_open_gates`) and deciding
 * it stay separate powers.
 */
export const OS_GATE_TOOLS: readonly BuiltInTool[] = [
    {
        name: GATE_SUMMARY,
        family: 'os.gate',
        summary: "Write what the person approving this run's next gate is deciding on.",
        description:
            'Write, for whoever approves this run’s next gate, what they are deciding on. Markdown bullets: what the action does, and anything to watch out for. Shown on the approval card in Gates and the run page. Required before os.mail_reply, and before any call whose gate the task marks requiresSummary (a merge, say) — the call is handed back until you write one. A later call replaces it, title, why and previewUrl included, but never rewrites a gate already waiting. Pass summary, and the card’s title, why and previewUrl when you have them.',
        inputSchema: {
            type: 'object',
            properties: {
                summary: {
                    type: 'string',
                    description:
                        'Markdown. Short headed sections of "- " bullets, e.g. "## What changed" then "## Watch out for". No preamble.',
                },
                title: {
                    type: 'string',
                    description:
                        'Optional. The card’s title: what approving does, in plain words, e.g. "Merge “Fix page titles” to production". One line, at most 120 characters. Without it the card is titled with the task.',
                },
                why: {
                    type: 'string',
                    description:
                        'Optional. One line under the title: why this waits for a person, e.g. "Leaves the building: changes the live app". At most 160 characters.',
                },
                previewUrl: {
                    type: 'string',
                    description:
                        'Optional. An https address the person can open to see the result before deciding: a pull request, a preview deploy, a draft. Shown as Preview on the card.',
                },
            },
            required: ['summary'],
            additionalProperties: false,
        },
        returns:
            'The summary, and any title, why and previewUrl, as stored. The gate copies them when it opens, so a later call never rewrites a gate already waiting or decided.',
        callers: ['run', 'api'],
        effect: 'write',
        retry: 'idempotent',
        retryNote: 'A later call replaces the summary, so a repeat leaves the same one.',
        // A vendor tool is never named here (#589): a task marks its own gate row `requiresSummary`.
        needs: { requiredBefore: ['os.mail_reply'] },
        errors: [
            { code: 'summary_required', when: '`summary` is missing or blank.', handling: 'tool_error' },
            { code: 'summary_too_short:40', when: '`summary` is under 40 characters.', handling: 'tool_error' },
            { code: 'summary_too_long:6000', when: '`summary` is over 6000 characters.', handling: 'tool_error' },
            { code: 'title_too_long:120', when: '`title` is over 120 characters.', handling: 'tool_error' },
            { code: 'title_one_line', when: '`title` runs over more than one line.', handling: 'tool_error' },
            { code: 'why_too_long:160', when: '`why` is over 160 characters.', handling: 'tool_error' },
            { code: 'why_one_line', when: '`why` runs over more than one line.', handling: 'tool_error' },
            {
                code: 'preview_url_not_https',
                when: '`previewUrl` is not an https address, or is over 2048 characters.',
                handling: 'tool_error',
            },
            {
                code: 'summary_needs_bullets',
                when: '`summary` has no markdown bullet (`- `, `* ` or `1. `).',
                handling: 'tool_error',
            },
            {
                code: 'gate_summary_required',
                when: 'A reply, or a call whose gate row is marked requiresSummary, arrives before this run has written a summary.',
                handling: 'hand_back',
                hint: GATE_SUMMARY_HINT,
            },
            {
                code: 'incomplete_read:<tool>',
                when: 'A paged read in this run ended incomplete (`tool_result_truncated` with `complete: false`: still too big at a page of 1, or its server ignores the page size), and that same target has not since been read through as pages 1 onward at one page size, ending in a short or empty page. Nothing is recorded; the refusal lists each such read with its tool, arguments and size.',
                handling: 'hand_back',
            },
        ],
        api: { method: 'POST', path: '/v1/runs/self/gate-summary', scope: 'run:self' },
        examples: [
            {
                input: {
                    summary:
                        '## What changed\n- Adds CSV export to the monthly report\n\n## Watch out for\n- A migration runs on release',
                    title: 'Merge “CSV export for the monthly report” to production',
                    why: 'Leaves the building: changes the live app',
                    previewUrl: 'https://example.com/pull/42',
                },
            },
        ],
        public: true,
    },
    {
        name: DECIDE_GATE,
        family: 'os.gate',
        summary: 'Decide one waiting gate as its Needs you card does: approve, reject, or send the work back.',
        description:
            'Decide one gate waiting on a person, as its card in Needs you does. Pass gateId as os.list_open_gates ' +
            'lists it, and decision: approve, reject or request_changes. A question gate (toolName os.ask_ceo) is ' +
            'approved by answering it: answers holds one row per question, { number, answer }, or ' +
            '{ number, requiresMoreDetail: true } to ask the run for more; a reject declines the whole ask and ' +
            'needs no answers. request_changes sends the work back with note as the feedback, only on a gate ' +
            'whose requestChanges is true. A gate that offers choices is decided through one: pass choice as ' +
            'its key, decision as the decision that choice records, and what the choice asks for in note (a time ' +
            'as ISO 8601). edit, with approve only, is the whole payload as it should run, changed only in the ' +
            'fields the gate’s editableFields names. The decision is recorded as you, and the run resumes, ' +
            'routes or ends exactly as it does from the portal. A gate already decided is refused.',
        inputSchema: {
            type: 'object',
            properties: {
                gateId: { type: 'string', description: 'The gate, by the id os.list_open_gates lists it under.' },
                decision: {
                    type: 'string',
                    enum: ['approve', 'reject', 'request_changes'],
                    description:
                        'approve runs the gated call, or answers the questions; reject declines it; request_changes sends the work back with note as the feedback.',
                },
                note: {
                    type: 'string',
                    description:
                        'Optional. The reason for a rejection, the feedback for request_changes (required then), or what a choice asks for: a time as ISO 8601 for a datetime input.',
                },
                answers: {
                    type: 'array',
                    items: { type: 'object' },
                    description:
                        'Approving a question gate: one object per question, { number, answer }, or { number, requiresMoreDetail: true } to ask for more. Every question the gate asks needs one.',
                },
                choice: {
                    type: 'string',
                    description:
                        'Optional. The key of the choice this decision is made through, from the gate’s choices; decision must be the decision that choice records.',
                },
                edit: {
                    type: 'object',
                    description:
                        'Optional, with approve only. The gate’s whole payload as it should run, changed only in the fields its editableFields names.',
                },
            },
            required: ['gateId', 'decision'],
            additionalProperties: false,
        },
        returns:
            'The decided gate: `status` (`approved`, `rejected` or `changes_requested`), `decidedAt`, `decisionNote`, `decisionChoice`, `answers` (each question with its answer, on an answered question gate) and `editedPayload` when an edit was sent, with the `runId`, `toolName` and `payload` it holds.',
        callers: ['api', 'mcp'],
        effect: 'write',
        sideEffects:
            'The run the gate holds moves: an approve runs the gated call or resumes the run with the answers, request_changes resumes it with the feedback, a rejected question resumes it with the refusal, and a rejected action ends its run as decision_rejected. A rejected mail gate closes the mail it held.',
        retry: 'idempotent',
        retryNote:
            'A decided gate is no longer waiting, so a repeat is refused with gate_not_waiting and decides nothing more.',
        errors: [
            {
                code: 'decision_required',
                when: '`decision` is missing, or not approve, reject or request_changes (400).',
                handling: 'tool_error',
            },
            {
                code: 'answers_must_be_array',
                when: '`answers` is given and is not a list (400).',
                handling: 'tool_error',
            },
            { code: 'gate_not_found', when: 'No gate by that id in your enterprise (404).', handling: 'tool_error' },
            {
                code: 'gate_not_waiting',
                when: 'The gate was already decided, or cancelled with its run (409).',
                handling: 'tool_error',
            },
            {
                code: 'answer_required:<number>',
                when: 'Approving a question gate with that question unanswered: no `answer` text and no `requiresMoreDetail` (400).',
                handling: 'tool_error',
            },
            {
                code: 'answer_unknown_question',
                when: 'An `answers` row names a question number the gate did not ask (400).',
                handling: 'tool_error',
            },
            {
                code: 'note_required',
                when: '`request_changes` without a `note`, or a choice whose input is required without one (400).',
                handling: 'tool_error',
            },
            {
                code: 'request_changes_not_routed',
                when: 'The gate cannot be sent back: its task routes no feedback, so `requestChanges` is false (409).',
                handling: 'tool_error',
            },
            {
                code: 'choice_unknown',
                when: '`choice` is not one the gate’s tool offers (400).',
                handling: 'tool_error',
            },
            {
                code: 'choice_decision_mismatch',
                when: '`decision` is not the decision that `choice` records (400).',
                handling: 'tool_error',
            },
            {
                code: 'note_not_a_time',
                when: 'A choice with a datetime input, and `note` is not a time (400).',
                handling: 'tool_error',
            },
            {
                code: 'edit_only_with_approve',
                when: '`edit` sent with reject or request_changes (400).',
                handling: 'tool_error',
            },
            {
                code: 'field_not_editable:<field>',
                when: '`edit` changes a field the gate’s `editableFields` does not name (400).',
                handling: 'tool_error',
            },
        ],
        api: { method: 'POST', path: '/v1/gates/:gateId/decision', scope: 'gates:write' },
        examples: [
            {
                input: {
                    gateId: '4b3e9c1a-0000-4000-8000-000000000001',
                    decision: 'approve',
                    answers: [{ number: 1, answer: 'Yes, schedule it now.' }],
                },
                note: 'Answering a question gate: every question the gate asks gets a row.',
            },
            {
                input: {
                    gateId: '4b3e9c1a-0000-4000-8000-000000000002',
                    decision: 'reject',
                    note: 'Not this quarter.',
                },
            },
            {
                input: {
                    gateId: '4b3e9c1a-0000-4000-8000-000000000003',
                    decision: 'request_changes',
                    note: 'Set the epic priority to High before filing.',
                },
            },
            {
                input: {
                    gateId: '4b3e9c1a-0000-4000-8000-000000000004',
                    decision: 'approve',
                    choice: 'snooze',
                    note: '2026-11-03T09:00:00Z',
                },
                note: 'A gate with choices, decided through one: the choice’s input goes in note.',
            },
        ],
        public: true,
    },
]
